import Radio, { RadioProps } from '@mui/material/Radio';
import FormControlLabel, {
  FormControlLabelProps
} from '@mui/material/FormControlLabel';
import { BaseReactComponent } from '../../../types';

import { useRadioGroup } from '@mui/material/RadioGroup';

export interface HiRadioProps extends BaseReactComponent {
  /**
   * The radio label.
   */
  label?: FormControlLabelProps['label'];
  /**
   *
   * The value of the component that will be returned by `onChange` callback. The DOM API casts this to a string.
   */
  value?: RadioProps['value'];
  /**
   * If `true`, the component is checked.
   */
  checked?: RadioProps['checked'];
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: RadioProps['disabled'];
  /**
   * Callback fired when the state is changed.
   */
  onChange?: RadioProps['onChange'];
  /**
   * Override Mui Radio props
   * (https://mui.com/material-ui/api/radio/).
   */
  RadioProps?: RadioProps;
}

/**
 * The `HiRadio` component has to be in a list of radio buttons, when there are two or more options that are mutually exclusive and the user must select **exactly one choice**.
 * In other words, clicking a non-selected radio button will deselect whatever other button was previously selected in the list.
 * - Lay out your lists **vertically**.
 * - Always offer a **default selection** for radio button lists.
 * - When an option isn’t available, make your radio button **disabled**.
 * - Don’t use radio button as action button.
 * - Try to be concise for the label.
 *
 * It extends Mui's `Radio` component.
 */
const HiRadio = ({
  label,
  value,
  checked,
  disabled,
  onChange,
  RadioProps,
  ...other
}: HiRadioProps) => {
  // If Radio Group exists around radio, get the value from radio group
  const radioGroup = useRadioGroup();
  const checkedValue = radioGroup ? radioGroup.value === value : checked;

  const RadioComponent = (
    <Radio
      {...other}
      {...RadioProps}
      checked={checkedValue}
      disabled={disabled}
      value={value}
      onChange={onChange}
      size="small"
      sx={{
        p: 2 // padding 8px
      }}
    />
  );

  // If label, render with FormControlLabel
  if (label) {
    return <FormControlLabel label={label} control={RadioComponent} />;
  }

  // If no label, render only Radio
  return RadioComponent;
};

export default HiRadio;
